(function () {
  const AXIS_THRESHOLD = 4;
  const AUTO_PX_PER_SEC = 22;

  function getGapPx(el) {
    const styles = getComputedStyle(el);
    const gapVal = styles.columnGap && styles.columnGap !== 'normal' ? styles.columnGap : styles.gap;
    const n = parseFloat(gapVal || '0');
    return Number.isNaN(n) ? 0 : n;
  }

  function initFooterLogos(root) {
    if (!root || root.dataset.footerLogosInit === 'true') return;

    const track = root.querySelector('[data-footer-logos-track]');
    const viewport = root.querySelector('[data-footer-logos-viewport]') || root;
    if (!track) return;

    const count = parseInt(root.dataset.logoCount, 10) || 0;
    if (count < 1) return;

    root.dataset.footerLogosInit = 'true';

    function bindImage(img) {
      if (!img) return;
      img.setAttribute('draggable', 'false');
      img.loading = 'eager';
      img.addEventListener('dragstart', (e) => e.preventDefault());
    }

    track.querySelectorAll('img').forEach(bindImage);

    let pos = 0;
    let contentWidth = 0;
    let firstHalf = 0;
    let isDragging = false;
    let didDrag = false;
    let hovering = false;
    let inView = true;
    let axis = null;
    let startX = 0;
    let startY = 0;
    let startPos = 0;
    let pointerId = null;
    let openedByPointer = false;
    let rafId = null;
    let lastTs = 0;
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    function setX(x) {
      pos = x;
      track.style.setProperty('--footer-logos-x', x + 'px');
    }

    function wrapPosition(x) {
      if (contentWidth <= 0) return x;
      while (x <= -contentWidth) x += contentWidth;
      while (x > 0) x -= contentWidth;
      return x;
    }

    function prefersReducedMotion() {
      return motionQuery.matches;
    }

    function canAutoplay() {
      return (
        contentWidth > 0 &&
        inView &&
        !isDragging &&
        !hovering &&
        !document.hidden &&
        !prefersReducedMotion()
      );
    }

    function stopAutoplay() {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      lastTs = 0;
    }

    function tick(ts) {
      if (!canAutoplay()) {
        rafId = null;
        lastTs = 0;
        return;
      }

      if (!lastTs) lastTs = ts;
      const dt = Math.min((ts - lastTs) / 1000, 0.064);
      lastTs = ts;
      setX(wrapPosition(pos + AUTO_PX_PER_SEC * dt));
      rafId = requestAnimationFrame(tick);
    }

    function startAutoplay() {
      if (rafId != null || !canAutoplay()) return;
      lastTs = 0;
      rafId = requestAnimationFrame(tick);
    }

    function originals() {
      return Array.from(track.children).slice(0, count);
    }

    function ensureCopies() {
      if (contentWidth <= 0) return;
      const viewportWidth = viewport.clientWidth;
      const copiesNeeded = Math.max(3, Math.ceil((contentWidth + viewportWidth) / contentWidth));
      const source = originals();
      while (Math.floor(track.children.length / count) < copiesNeeded) {
        source.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          const cloneLinks = clone.matches('a') ? [clone] : Array.from(clone.querySelectorAll('a'));
          cloneLinks.forEach((link) => {
            link.setAttribute('tabindex', '-1');
          });
          const img = clone.querySelector('img');
          if (img) {
            img.alt = '';
            bindImage(img);
          }
          track.appendChild(clone);
        });
      }
    }

    function measure() {
      const items = originals();
      if (!items.length) return;

      const gap = getGapPx(track);
      let width = 0;
      items.forEach((item) => {
        width += item.getBoundingClientRect().width + gap;
      });
      contentWidth = width;
      firstHalf = items[0].getBoundingClientRect().width / 2;
      ensureCopies();
    }

    function resetToCroppedStart() {
      setX(wrapPosition(-firstHalf));
    }

    function linkFromEvent(e) {
      const node = e && e.target;
      if (!node || typeof node.closest !== 'function') return null;
      return node.closest('a[href]');
    }

    function onPointerDown(e) {
      if (e.button !== 0) return;
      isDragging = true;
      didDrag = false;
      openedByPointer = false;
      axis = null;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      startPos = pos;
      stopAutoplay();
    }

    function onPointerMove(e) {
      if (!isDragging || e.pointerId !== pointerId) return;

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (!axis) {
        if (Math.abs(dx) < AXIS_THRESHOLD && Math.abs(dy) < AXIS_THRESHOLD) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'x') {
          root.classList.add('is-dragging');
          try {
            track.setPointerCapture(e.pointerId);
          } catch (_) {}
        } else {
          isDragging = false;
          pointerId = null;
          startAutoplay();
          return;
        }
      }

      if (axis !== 'x') return;
      didDrag = true;
      e.preventDefault();
      setX(wrapPosition(startPos + dx));
    }

    function onClick(e) {
      if (!didDrag && !openedByPointer) return;
      e.preventDefault();
      e.stopPropagation();
      didDrag = false;
      openedByPointer = false;
    }

    function onDragStart(e) {
      e.preventDefault();
    }

    function endDrag(e) {
      if (pointerId != null && e && e.pointerId !== pointerId) return;
      const shouldOpen = !didDrag;
      const link = shouldOpen ? linkFromEvent(e) : null;
      isDragging = false;
      axis = null;
      pointerId = null;
      root.classList.remove('is-dragging');
      if (link) {
        openedByPointer = true;
        window.open(link.href, '_blank', 'noopener,noreferrer');
      }
      startAutoplay();
    }

    function onWheel(e) {
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.shiftKey ? e.deltaY : 0;
      if (!delta) return;
      e.preventDefault();
      setX(wrapPosition(pos - delta));
    }

    function onPointerEnter(e) {
      if (e.pointerType !== 'mouse') return;
      hovering = true;
      stopAutoplay();
    }

    function onPointerLeave() {
      hovering = false;
      startAutoplay();
    }

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove, { passive: false });
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('click', onClick, true);
    track.addEventListener('dragstart', onDragStart);
    viewport.addEventListener('pointerenter', onPointerEnter);
    viewport.addEventListener('pointerleave', onPointerLeave);
    root.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });
    if (typeof motionQuery.addEventListener === 'function') {
      motionQuery.addEventListener('change', () => {
        if (prefersReducedMotion()) stopAutoplay();
        else startAutoplay();
      });
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          inView = entries.some((entry) => entry.isIntersecting);
          if (inView) startAutoplay();
          else stopAutoplay();
        },
        { threshold: 0.01 }
      );
      observer.observe(root);
    }

    function imagesReady() {
      const images = Array.from(track.querySelectorAll('img'));
      return Promise.all(
        images.map((img) => {
          if (typeof img.decode === 'function') return img.decode().catch(() => {});
          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          });
        })
      );
    }

    function ready() {
      measure();
      resetToCroppedStart();
      startAutoplay();
    }

    imagesReady().then(ready);

    window.addEventListener('resize', () => {
      const currentRatio = contentWidth ? pos / contentWidth : 0;
      measure();
      if (Math.abs(pos + firstHalf) < 1) {
        resetToCroppedStart();
      } else {
        setX(wrapPosition(currentRatio * contentWidth));
      }
      startAutoplay();
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-footer-logos]').forEach(initFooterLogos);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
